/**
 * Scroll-Zustand der Filterleisten (.highlight-selector).
 *
 * Die Leiste bricht nicht mehr um, sondern scrollt horizontal — ohne Hinweis
 * merkt das niemand, bevor er zufällig wischt. Dieses Modul setzt den
 * tatsächlichen Zustand als Klassen auf die Leiste, damit das CSS nur dort
 * abblendet und Pfeile zeigt, wo wirklich etwas verborgen ist:
 *
 *   is-scrollable   überhaupt Überlauf vorhanden
 *   has-scroll-start  links ist noch etwas verborgen
 *   has-scroll-end    rechts ist noch etwas verborgen
 *
 * Rein zustandsgesteuert statt per Breakpoint: Eine Leiste, die passt, zeigt
 * nie einen Hinweis — auch nicht auf schmalen Fenstern am Desktop.
 */
document.addEventListener("DOMContentLoaded", () => {
  const selectors = Array.from(
    document.querySelectorAll(".highlight-selector")
  ).filter((selector) => selector.querySelector(".highlight-selector__rail"));

  if (selectors.length === 0) {
    return;
  }

  // Subpixel-Toleranz: scrollWidth/clientWidth runden unterschiedlich, ohne
  // Puffer flackert der Endwinkel am letzten Pixel.
  const tolerance = 2;
  // Unterhalb davon lohnt der Hinweis nicht: Ein Überlauf von wenigen Pixeln
  // (gemessen 6px bei 600px Fensterbreite) verbirgt nichts, was man suchen
  // müsste — Abblendung und Winkel wären dann nur Lärm.
  const minOverflow = 14;
  const prefersReducedMotion =
    typeof window.matchMedia === "function" &&
    window.matchMedia("(prefers-reduced-motion: reduce)").matches;

  // Während des Anstupsers keine Startmarkierung setzen — sonst blitzt der
  // linke Winkel für eine halbe Sekunde auf und sieht nach Fehler aus.
  const nudging = new WeakSet();

  const sync = (selector) => {
    const rail = selector.querySelector(".highlight-selector__rail");

    if (!rail) {
      return;
    }

    const maxScroll = rail.scrollWidth - rail.clientWidth;
    const isScrollable = maxScroll > minOverflow;

    selector.classList.toggle("is-scrollable", isScrollable);
    selector.classList.toggle(
      "has-scroll-start",
      isScrollable && !nudging.has(selector) && rail.scrollLeft > tolerance
    );
    selector.classList.toggle(
      "has-scroll-end",
      isScrollable && rail.scrollLeft < maxScroll - tolerance
    );
  };

  // Einmaliger Anstupser, sobald die Leiste ins Bild kommt: Abblendung und
  // Winkel sagen „da ist mehr", die Bewegung sagt „und zwar durch Wischen".
  // Nur wenn wirklich Überlauf da ist, nur am Anfang der Leiste, nur einmal.
  const nudge = (selector) => {
    const rail = selector.querySelector(".highlight-selector__rail");

    if (
      prefersReducedMotion ||
      !selector.classList.contains("is-scrollable") ||
      typeof rail.scrollTo !== "function" ||
      rail.scrollLeft > tolerance
    ) {
      return;
    }

    nudging.add(selector);
    // Ohne `is-nudging` (schaltet scroll-snap ab) zieht der Snap-Punkt die
    // Leiste sofort zurück — gemessen kam sie dann nur 13px weit statt 30.
    rail.classList.add("is-nudging");
    rail.scrollTo({ left: 30, behavior: "smooth" });

    window.setTimeout(() => {
      rail.scrollTo({ left: 0, behavior: "smooth" });
      window.setTimeout(() => {
        rail.classList.remove("is-nudging");
        nudging.delete(selector);
        sync(selector);
      }, 500);
    }, 600);
  };

  selectors.forEach((selector) => {
    const rail = selector.querySelector(".highlight-selector__rail");

    sync(selector);

    rail.addEventListener("scroll", () => sync(selector), { passive: true });

    // Die Reiterbreite hängt an der geladenen Schrift und an der
    // Containerbreite — beides ändert sich nach dem ersten sync.
    if ("ResizeObserver" in window) {
      new ResizeObserver(() => sync(selector)).observe(rail);
    } else {
      window.addEventListener("resize", () => sync(selector));
    }
  });

  if (document.fonts && typeof document.fonts.ready?.then === "function") {
    document.fonts.ready.then(() => selectors.forEach(sync));
  }

  if ("IntersectionObserver" in window) {
    const nudgeObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) {
            return;
          }

          nudgeObserver.unobserve(entry.target);
          // Kurz warten: Beim Hereinscrollen soll die Leiste erst stehen,
          // bevor sie sich bewegt — sonst geht der Hinweis unter.
          window.setTimeout(() => nudge(entry.target), 420);
        });
      },
      { threshold: 0.9 }
    );

    selectors.forEach((selector) => nudgeObserver.observe(selector));
  }
});
